import type { NextPage } from 'next';
import { useCallback, useEffect, useMemo } from 'react';
import { SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import { Category, PartnerMarketplaceStatus } from '@prisma/client';
import { Button } from '@components/Button';
import { Container } from '@components/Container';
import { FormHeader, FormSection, FormWrapper } from '@components/Form';
import { Input, Label } from '@components/Input';
import { useUserProfile } from '@hooks/useUserProfile';
import { AuthLayout } from '@layouts/AuthLayout';
import { useNotification } from '@providers/NotificationProvider';
import { updateProfile } from '@utils/api';
import { isErrorMessage } from '@utils/error';
import { updateProfileBody, UpdateProfileBody } from '@validations/update-profile';
import { Toggle } from '@components/Toggle';
import { SelectCoverModal } from '@components/CoverBanner';
import { categoryLabels } from '@utils/category';
import { TagSelect } from '@components/TagSelect';
import { GreenAlert } from '@components/Alert';
import { useModal } from '@components/Modal';
import { parseImage } from '@utils/image';
import { PartnerCard, usePartnerModal } from '@components/Partners';
import { useSaveUserImage } from '@hooks/useSaveUserImage';
import { LoadingContent } from '@components/LoadingContent';

const Profile: NextPage = () => {
  return (
    <AuthLayout userType="affiliate" coloredBorder navSelected="Profile">
      <ProfileContent />
    </AuthLayout>
  );
};

function ProfileContent() {
  const useUserProfileRes = useUserProfile();
  const { data, error, isValidating } = useUserProfileRes;

  return (
    <LoadingContent error={error} loading={isValidating && !data}>
      <Container size="2xl">
        <div className="pb-20">{data && <ProfileForm useUserProfileRes={useUserProfileRes} />}</div>
      </Container>
    </LoadingContent>
  );
}

export const ProfileForm = (props: {
  useUserProfileRes: Awaited<ReturnType<typeof useUserProfile>>;
}) => {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
    control,
    watch,
  } = useForm<UpdateProfileBody>({
    resolver: zodResolver(updateProfileBody),
  });
  const { showNotification } = useNotification();

  const { data } = props.useUserProfileRes;

  const onSubmit: SubmitHandler<UpdateProfileBody> = useCallback(
    async data => {
      const showError = () =>
        showNotification({
          type: 'error',
          description: `There was an error updating your profile`,
        });

      try {
        const res = await updateProfile(data);
        if (isErrorMessage(res)) showError();
        else showNotification({ type: 'success', description: `Profile updated!` });
      } catch (error) {
        showError();
      }
    },
    [showNotification]
  );

  const { fields, append, remove } = useFieldArray({ name: 'inviteCodes', control });
  const {
    fields: fieldsCategories,
    append: appendCategories,
    remove: removeCategories,
  } = useFieldArray({ name: 'categories', control });

  const { isModalOpen, openModal, closeModal } = useModal();
  const {
    uploadImage,
    url: imageUrl,
    loading: imageLoading,
  } = useSaveUserImage({ imageUrl: data?.coverPictureUrl });
  const coverImage = useMemo(() => parseImage(imageUrl), [imageUrl]);

  useEffect(() => {
    if (data) {
      setValue('nickname', data.nickname ?? undefined);
      setValue('about', data.about ?? undefined);
      setValue('twitter', data.twitter ?? undefined);
      setValue('discord', data.discord ?? undefined);
      setValue('discordServerInvite', data.discordServerInvite ?? undefined);
      setValue('instagram', data.instagram ?? undefined);
      setValue('tiktok', data.tiktok ?? undefined);
      setValue('email', data.email ?? undefined);
      setValue('payoutAddress', data.payoutAddress ?? undefined);
      setValue('inviteCodes', data.inviteCodes?.map(code => ({ value: code })) ?? undefined);
      setValue('categories', data.categories?.map(category => ({ value: category })) ?? undefined);
      setValue(
        'activatePartnerMarketplace',
        (data.partnerMarketplaceStatus === PartnerMarketplaceStatus.PUBLIC ||
          data.partnerMarketplaceStatus === PartnerMarketplaceStatus.PENDING) ??
          false
      );
    }
  }, [data, setValue]);

  const { setSelectedPartner, PartnerModal } = usePartnerModal({});

  const nickname = watch('nickname');
  const about = watch('about');
  const twitter = watch('twitter');
  const discordServerInvite = watch('discordServerInvite');

  const partnerCardPartner = useMemo(
    () => ({
      ...data,
      nickname: nickname || 'Name',
      about: about || 'About',
      categories: fieldsCategories.map(field => field.value as Category) || [],
      address: '',
      twitterFollowerCount: data?.twitterFollowerCount || 0,
      discordServerMemberCount: data?.discordServerMemberCount || 0,
      twitter: twitter || '',
      discordServerInvite: discordServerInvite || '',
      coverPictureUrl: coverImage.imageUrl || '',
      partnerStatus: null,
    }),
    [about, coverImage.imageUrl, data, discordServerInvite, fieldsCategories, nickname, twitter]
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="mt-8">
      <FormWrapper>
        <FormSection first>
          <FormHeader
            title="Profile"
            description="This information will be shared with projects."
          />
          <Input
            type="text"
            name="nickname"
            label="Name"
            registerProps={register('nickname')}
            explainText="This can be your real name, project name, or an anonymous username."
            error={errors.nickname}
            condensed
          />
          <Input
            type="text"
            as="textarea"
            rows={4}
            name="about"
            label="About"
            registerProps={register('about')}
            explainText="Tell projects what you do and how you can help share their project."
            error={errors.about}
          />
          <Input
            type="text"
            name="twitter"
            label="Twitter handle"
            leftText="https://twitter.com/"
            registerProps={register('twitter')}
            error={errors.twitter}
            condensed
          />
          <Input
            type="text"
            name="discordServerInvite"
            label="Discord server invite URL"
            leftText="https://discord.com/invite/"
            registerProps={register('discordServerInvite')}
            error={errors.discordServerInvite}
            condensed
          />
          <Input
            type="text"
            name="instagram"
            label="Instagram handle"
            leftText="https://instagram.com/"
            registerProps={register('instagram')}
            error={errors.instagram}
            condensed
          />
          <Input
            type="text"
            name="tiktok"
            label="Tik Tok handle"
            leftText="https://tiktok.com/@"
            registerProps={register('tiktok')}
            error={errors.tiktok}
            condensed
          />
          <Input
            type="text"
            name="discord"
            label="Discord handle"
            registerProps={register('discord')}
            error={errors.discord}
            condensed
            explainText={`If you'd like to be contacted on Discord`}
          />
        </FormSection>
        <FormSection first={false}>
          <FormHeader
            title="Partner Program"
            description="Get listed as ShareMint partner network. Projects will reach out to you to work together."
          />
          <Toggle
            name="activatePartnerMarketplace"
            label="List in partner gallery"
            enabled={!!watch('activatePartnerMarketplace')}
            onChange={value => setValue('activatePartnerMarketplace', value)}
          />
          {watch('activatePartnerMarketplace') &&
            data?.partnerMarketplaceStatus !== PartnerMarketplaceStatus.PUBLIC && (
              <GreenAlert>
                We will notify you via email once approved for the partner program.
              </GreenAlert>
            )}

          <div className="relative">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              className={clsx(
                'h-44 w-full rounded object-cover lg:h-48',
                coverImage.gradient?.direction,
                coverImage.gradient?.colors,
                {
                  'bg-blue-50': !coverImage.gradient && !coverImage.color,
                }
              )}
              style={{ backgroundColor: coverImage.color }}
              src={coverImage.imageUrl}
              alt=""
            />
            <div className="group">
              <label
                htmlFor="cover-photo-upload"
                className="absolute top-0 flex h-44 w-full cursor-pointer items-center justify-center rounded transition-all group-hover:bg-black/[.5] lg:h-48"
                onClick={openModal}
              >
                <span
                  className={clsx(
                    'text-xl text-gray-700 transition-all group-hover:inline group-hover:text-white',
                    {
                      hidden: imageUrl,
                    }
                  )}
                >
                  Upload Image
                </span>
              </label>

              {imageLoading && (
                <div className="absolute top-0 flex h-32 w-full items-center justify-center bg-black/[.5] lg:h-48">
                  <span className="text-xl text-white">Loading...</span>
                </div>
              )}
            </div>
          </div>

          <div>
            <Label label="Categories" name="categories" />
            <TagSelect
              tags={Object.entries(categoryLabels)}
              selected={fieldsCategories}
              onClick={(category, selected) => {
                if (selected) {
                  const index = fieldsCategories.findIndex(field => field.value === category);
                  removeCategories(index);
                } else {
                  appendCategories({ value: category });
                }
              }}
            />
          </div>

          <SelectCoverModal
            onSuccess={async () => {
              await uploadImage();
              closeModal();
            }}
            onError={() => {
              showNotification({
                type: 'error',
                description: 'There was an error uploading a cover image.',
              });
            }}
            isModalOpen={isModalOpen}
            closeModal={closeModal}
            coverImage={coverImage}
            disableColorGradient
          />

          <div>
            <p className="text-sm text-gray-500">
              This is what your profile will look like to projects that want to partner with you:
            </p>
            <div className="mt-1">
              <div className="max-w-xs">
                <PartnerCard
                  partner={partnerCardPartner}
                  truncateDescription
                  singleApply={async () => {}}
                  singleApplyDisabled
                  onClickView={() => setSelectedPartner(partnerCardPartner)}
                />
              </div>
            </div>
          </div>
          <PartnerModal />
        </FormSection>
        <FormSection first={false}>
          <FormHeader
            title="Referral Code"
            description="The code to be used in your referral link."
          />
          {fields.map((f, i) => {
            return (
              <Input
                key={f.id}
                type="text"
                name={`inviteCodes.${i}.value`}
                label={i === 0 ? 'Referral codes' : ''}
                registerProps={register(`inviteCodes.${i}.value`)}
                leftText="https://sharemint.xyz/cool-project?r="
                error={errors.inviteCodes?.[i]?.value}
                onClickAdd={() => append({ value: '' })}
                onClickRemove={fields.length > 1 ? () => remove(i) : undefined}
              />
            );
          })}
        </FormSection>
        <FormSection first={false}>
          <FormHeader
            title="Contact Details"
            description="This information will be used by ShareMint to contact you about your earnings and new projects."
          />
          <Input
            type="email"
            name="email"
            label="Email"
            registerProps={register('email')}
            error={errors.email}
            condensed
          />
        </FormSection>
        <FormSection first={false}>
          <FormHeader
            title="Payout Details"
            description="If you'd like to be paid to a different address."
          />
          <Input
            type="text"
            name="payoutAddress"
            label="Ethereum Address"
            registerProps={register('payoutAddress')}
            error={errors.payoutAddress}
            condensed
          />
        </FormSection>
        <div className="flex justify-end pt-4">
          <Button type="submit" color="gradient" loading={isSubmitting}>
            Save
          </Button>
        </div>
      </FormWrapper>
    </form>
  );
};

export default Profile;
